import React from "react";
import {
  Box,
  useTheme,
  Typography,
  Card,
  CardContent,
  CardActionArea,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CardDashboardItemVerticalMulti from "components/CardDashboardItemVerticalMulti";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";

const Help = () => {
  usePageTitle("Help");
  const theme = useTheme();

  // Array of tutorial videos organized by category
  const tutorialVideos = [
    {
      category: "Getting Started",
      videos: [
        {
          name: "Introduction to CE Portal & Dashboard",
          description:
            "Learn the basics of navigating the CE Portal interface, as well as the Dashboards for each service.",
          link: "https://youtu.be/V2P398FY05Y",
          thumbnailUrl: "https://img.youtube.com/vi/example1/mqdefault.jpg",
        },
      ],
    },
    {
      category: "Services: Interpretations",
      videos: [
        {
          name: "Add Interpretation Assignment",
          description:
            "How to create and set up a new interpretation assignment",
          link: "https://youtu.be/UbDJMS89flE",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
        {
          name: "Edit Interpretation Assignment",
          description: "How to edit a interpretation assignment",
          link: "https://youtu.be/Eccb1hF3064",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
        {
          name: "All Interpretations",
          description: "Browsing through all the interpretation assignments",
          link: "https://youtu.be/eVn1LMym0dw",
          thumbnailUrl: "https://img.youtube.com/vi/example6/mqdefault.jpg",
        },
      ],
    },
    {
      category: "Services: Translations",
      videos: [
        {
          name: "Add Translation Assignment",
          description: "How to create and set up a new translation assignment",
          link: "https://youtu.be/fVgh_W0OxYU",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
        {
          name: "Edit Translation Assignment",
          description: "How to edit a translation assignment",
          link: "https://youtu.be/xOJMkINcRr8",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
        {
          name: "All Translations",
          description: "Browsing through all the translation assignments",
          link: "https://youtu.be/Sr3v8D6kJp0",
          thumbnailUrl: "https://img.youtube.com/vi/example6/mqdefault.jpg",
        },
      ],
    },
    {
      category: "Services: Other",
      videos: [
        {
          name: "All about the Other Assignments",
          description: "Create, Edit, and Browse through the Other Assignments",
          link: "https://youtu.be/f7McCAA1uPQ",
          thumbnailUrl: "https://img.youtube.com/vi/example5/mqdefault.jpg",
        },
      ],
    },
    {
      category: "Connections",
      videos: [
        {
          name: "Linguists",
          description: "Add, Edit, and Check Linguists Details",
          link: "https://youtu.be/I8l9s4Aq7W0",
          thumbnailUrl: "https://img.youtube.com/vi/example7/mqdefault.jpg",
        },
        {
          name: "Clients",
          description: "Add, Edit, and Check Clients Details",
          link: "https://youtu.be/CSlfZ0RKJnc",
          thumbnailUrl: "https://img.youtube.com/vi/example8/mqdefault.jpg",
        },
      ],
    },
  ];

  // Array to store version history and changes
  const versions = [
    {
      version: "0.9.24",
      date: "05-12-2026",
      changes: [
        "Fixed an issue that caused the Project Number in Translation and Other Services to change unexpectedly.",
        "Fixed an issue that caused Mileage Roundtrip discrepancies.",
        
      ],
    },
    {
      version: "0.9.23",
      date: "05-11-2026",
      changes: [
        "Added Timesheets to allow the users to track their time using the Portal.",
        "Fixed an issue that caused the mileage to be automatically added to the assignment's cost upon creation.",
        "Added a way to copy the Linguist email from the overlay to send assignment details.",
        "Added 'Watermark PDF' feature under Translations > Documents.",
      ],
    },
    {
      version: "0.9.22",
      date: "05-08-2026",
      changes: [
        "Added the ability to merge duplicate clients.",
        "Added the ability to merge duplicate Linguists.",
        "Fixed an issue that caused Duplicated assignments to have incorrect costs.",
        "Duplicating assignments, now prefills the data into the New Assignment form, allowing users to easily duplicate and edit assignments.",
        "Added the ability to change email, with the necessary safety checks and confirmations.",
        "Added Search history in the Global Search, allowing users to easily access their recent searches.",
        "Global Search now allows searching by language.",
        "The projects in the Linguist and Client Profiles now only show the the months and years where the linguist/client had assignments, instead of showing all months and years.",
      ],
    },
    {
      version: "0.9.21",
      date: "05-07-2026",
      changes: [
        "Address changes to Clients and Linguists are now properly saved into the History",
        "Corrected an issue that caused Service Costs to not show in the Service History.",
        "Added the ability to merge duplicate requestors.",
        "Updated the Client and Linguist Profiles UI to be more user-friendly and intuitive.",
      ],
    },
    {
      version: "0.9.20",
      date: "05-06-2026",
      changes: [
        "Added a Global Search feature: Allowing users to search across all entities in the portal.",
        "Added a 'Quick Create' button to quickly create new services, clients, or linguists from anywhere in the portal.",
        "Updated the Dashboard to show more insights and KPIs, as well as quick links to important sections of the portal.",
        "Address changes to Clients and Linguists are now properly saved into the History",
        "Corrected an issue that caused Service Costs to not show in the Service History.",
        "Added the ability to merge duplicate requestors.",
      ],
    },
    {
      version: "0.9.19",
      date: "05-04-2026",
      changes: [
        "Fixed some bugs with the invoices.",
      ],
    },
    {
      version: "0.9.18",
      date: "04-29-2026",
      changes: [
        "Added Service Types to Settings; Allowing users to add or edit Service Types for different Service Categories.",
        "When creating a new Interpretation, Linguists will now show the number of assignments they had with that Client.",
        "When creating a new Interpretation, Languages will now show the number of assignments that client had.",
        "Added 'Pre-Assign Outreach' feature to Interpretations list: Allowing users to select a client, a language, and an address, and see the list of linguists with distance from the assignment, as well as being able to call, or move forward with the linguist to create the assignment.",
      ],
    },
    {
      version: "0.9.17",
      date: "04-27-2026",
      changes: [
        "Fixed an issue that caused Account Charge to not display correctly.",
        "Updated Address for Linguists and Clients to be split into multiple lines.",
        
      ],
    },
    {
      version: "0.9.16",
      date: "04-24-2026",
      changes: [
        "Fixed an issue that caused Times in Interpretations list to show as N/A.",
        "Fixed various Calculation issues in Interpretation Creation.",
        "Corrected an issue that caused mileage and other costs to not show correctly in the Interpretation Profile.",
        "Updated the Time Picker to better show AM/PM selection.",
        "Various bug fixes and Quality of Life improvements.",
        "Improved the Reminders to include/exclude requested assignments.",
        "Improved the Documents Encryption process.",
      ],
    },
    {
      version: "0.9.15",
      date: "04-17-2026",
      changes: [
        "Improved the Costs calculation in all services.",
        "Added 'Address', 'Mileage Roundstip', and 'Mileage Cost' to the Interpretations list.",
      ],
    },
    {
      version: "0.9.14",
      date: "04-15-2026",
      changes: [
        "Added 'PDF Protection' feature to the Portal: Allowing users to set upload PDFs and protect them with passwords.",
        "Improved Contact Persons: More than a single contact person can be added to a service.",
        "Updated the Requestors to have a dedicated field for the Phone number Extension, and ensured that the call buttons handle the ext by default.",
        "Fixed an error that caused the Portal to crash when trying to add a new Task.",
        "Bug fixes, issues corrections, and performance improvements.",
      ],
    },
    {
      version: "0.9.13",
      date: "04-14-2026",
      changes: [
        "Improved the Options in the Creation and Editing overlay of all 3 services to be more user-friendly and intuitive.",
        "Corrected an issue that caused OPI assignments to be added to Outlook Calendar.",
        "Bug fixes, issues corrections, and performance improvements.",
      ],
    },
    {
      version: "0.9.12",
      date: "04-08-2026",
      changes: [
        "Improved the Clients and Linguists to have multiple notes.",
        "Added 'Invoice Note' to Client Profile, allowing users to set a default note for each client that will be added to the invoice when the client is selected.",
        "Added Payment Method Notes for invoices; Allowing invoices to have specific notes depending on Paymenth method for each client.",
      ],
    },
    {
      version: "0.9.11",
      date: "04-06-2026",
      changes: [
        "Added 'Service Documents': Allowing users to upload document templates, and quickly fill them when needed.",
        "Added 'Encrypt & Decrypt' feature to quickly hide away any sensitive information from the document, and add it back after translation.",
        "Added 'Reports Insights': A new overlay that provides deterministic insights based on the selected report filters, to help users understand the data and make informed decisions.",
      ],
    },
    {
      version: "0.9.10",
      date: "04-03-2026",
      changes: [
        "Various bug fixes and performance improvements across the whole Portal.",
      ],
    },
    {
      version: "0.9.09",
      date: "04-02-2026",
      changes: [
        //"Added 'Service Documents': Allowing users to upload document templates, and quickly fill them when needed.",
        "Various performance improvements and bug fixes in the Invoices module.",
      ],
    },
    {
      version: "0.9.08",
      date: "04-01-2026",
      changes: [
        "Added Type column to the Interpretations list to easily distinguish between Interpretation Types.",
        "Various performance improvements and bug fixes across the whole Portal.",
      ],
    },
    {
      version: "0.9.07",
      date: "03-31-2026",
      changes: [
        "Added Advanced Analytics reports: Growth & Business Trends, Service Mix & Efficiency, Pricing & Rate Intelligence, Demand, Churn & Forecast, and Market & Growth Opportunities.",
        "Expanded reports interactions with drill-down support and improved chart usability across bars, lines, and pies.",
        "Improved report chart wrappers with better legend placement, spacing, and click behavior to avoid overlap and improve readability.",
        "Added nested pie visualization for revenue mix by category and service type, with clearer color contrast.",
        "Improved report and dashboard tooltip quality, including glossary-enriched explanations for key terms and metrics.",
        "Updated Dashboard General KPI trend percentages to show period-over-period change, and added percentage tooltips with period labels, value change, and percent change context.",
      ],
    },
    {
      version: "0.9.06",
      date: "03-27-2026",
      changes: [
        "Added 'Notifications' feature.",
        "Users can create Notification rules from Settings: Setting up notifications based on specific triggers and conditions related to services and deadlines.",
        "Users can change the Notification sound from Settings.",
        "Added 'Users Tag System': Users can tag other users [using @username] in notes and tasks to notify them and keep them in the loop.",
        "Added 'Service Tag System': Users can tag services [using #projectNumber] in notes and tasks to link them to the service and easily access them.",
      ],
    },
    {
      version: "0.9.05",
      date: "03-19-2026",
      changes: [
        "Fixed an issue that caused Filtering columns in different Data Grids across the Portal to crash.",
        "Adjust Translation and Other Services creation to include Payment Fees, and Additional Linguist Cost (if any).",
        "Generated Documents now show the templates that a merged document consists off.",
        "Corrected an issue that caused incorect values of newly added Linguist Requirements.",
        "Corrected an issue that prevented newly created Linguist Requirements from showing as columns in the Linguists list.",
      ],
    },
    {
      version: "0.9.04",
      date: "03-18-2026",
      changes: [
        "Corrected an issue that caused incorrect Calculations when re-opening assignments marked as Short (< 1 hour).",
        "Interpretation Options are now visible when Completing assignments.",
        "Corrected issues with Reminder Text Message formatting when using the 'Send' button.",
        "Improved Interpretation Details that's generated in the Assignment profile.",
        "Added filtering by Month, and by Expense Status for Expenses.",
        "Added History Tab to Linguist and Client profiles.",
        "When creating invoices, Payment Link is requested to fill for clients with Credit Card as Payment Method.",
        "Client Payment Methods allows better management for clients payments and invoices.",
        "Added 'Payment Methods' and 'Service Types' to Settings > Clients, allowing users to create and edit them.",
        "Added quick links to open Client profile from the Invoice.",
      ],
    },
    {
      version: "0.9.03",
      date: "03-17-2026",
      changes: [
        "Corrected various issues in the Interpretation Creation process.",
        "Changed 'Delete Member' to 'Disable Member', allowing admins to disable accounts without deleting them.",
        "Corrected an issue that caused Expenses to not be created as Finalzied, only as Draft.",
        "Made the list of Expenses showing on hover split between Draft and Finalized.",
        "Added the Grid Options to Linguists Payments: Density, Columns, and Export.",
        "Corrected an issue that caused the Enter key to create an incomplete service.",
      ],
    },
    {
      version: "0.9.02",
      date: "03-16-2026",
      changes: ["Performance improvements."],
    },
    {
      version: "0.9.01",
      date: "03-13-2026",
      changes: [
        "Updated the Grid Options: Columns: Added a button to reset the column changes back to default.",
        "Added a way to Call or Email the requestor from the Interpretation Profile.",
        "Added a feature to prefill Target Language to English if Source Language is not English in Translation Creation.",
        "Corrected an issue in the Linguists Payments that caused the list of services related to a single linguist to not scroll down.",
        "Updated the Settings UI to show the different options under categories for ease of access.",
      ],
    },
    {
      version: "0.9.00",
      date: "03-12-2026",
      changes: [
        "Updated the Linguist Requirements data structure to allow adding new fields as needed.",
        "Added the UI to add or edit Linguist Requirements from the Settings.",
        "Added the 'Document Templates' in the Settings: Allows users to upload a fillable PDF to the Portal.",
        "The Document Template creation screen: Allows linking specific PDF fields to Linguist data.",
        "After the Document Template is created, it can be used to generate a filled PDF for each linguist, with their data automatically filled in the linked fields.",
        "Documents can be generated for multiple selected linguists at once, or for a single linguist from their profile.",
        "Generated Documents can be accessed from the linguists screen for all linguists, or from the linguist profile for a single linguist.",
        "Reports Updates:",
        "- Added the following Business Financial Reports: Expense Trends & Profit & Loss Statement.",
        "- Added the following Client Reports: Client Overview, Client Directory & Contracts, Client Activity & Service Mix, Client Revenue & Profitability, and Client Retention & Risk.",
        "- Added the following Linguist Reports: Linguist Overview.",
        "- Expanded reports UX with dashboard cards, chart visualizations, and side drilldown drawers for detailed record-level analysis.",
        "- Added New Clients and Retained Clients click-to-open drilldowns in Client Revenue & Profitability.",
        "- Implemented and hardened Client Retention & Risk logic by combining retention details, payment ranges, client activity, and outstanding invoices.",
        "- Corrected payment-risk reporting issues (slow payer counts, average payment-day display, duplicate merges, and inclusive risk criteria for overdue or delayed payers).",
        "- Updated payment-ranges backend aggregation so invoice count/revenue and average-day calculations are more accurate and consistent for reporting.",
      ],
    },
    {
      version: "0.8.22",
      date: "02-26-2026",
      changes: [
        "Added the following Invoices & AR Reports: Outstanding Invoices & Invoice Timeline.",
        "Added the following Financial Reports: Profit Margin Analysis.",
        "Corrected an issue that caused the Duplicate Assignment dialog to click more than once, resulting in more than 1 duplicate.",
      ],
    },
    {
      version: "0.8.21",
      date: "02-25-2026",
      changes: [
        "Ensured that all old data [2023 | 2024 | 2025] is perfectly synced between Portal and Tracker.",
        "Added a safety check dialog before updating assignments that are marked as PAID.",
        "Various UI updates.",
        "Added a feature that updates the future assignments of a linguist when their rate is updated.",
        "Added a feature to generate one service per language for Translations and Other Services assignments with multiple target languages.",
        "Added the Density, Columns, and Export options to Linguists.",
        "Added the Density, Columns, and Export options to Invoices.",
        "Fixed an issue that caused services to fail to export.",
        "Updated the UI of the Service History to make it more user-friendly.",
        "Updated the Service History to use the updated Audit Log.",
      ],
    },
    {
      version: "0.8.20",
      date: "02-24-2026",
      changes: [
        "Updated the Expenses Categories and Subcategories to be simpler and more intuitive.",
        "Added quick action buttons in the Linguist and Client profiles to copy, call, text, or email the contact directly from the portal.",
        "Updated Expenses to have a status of Draft or Finalized, and the ability to finalize expenses for a whole month or separately.",
        "Corrected an issue that caused data discrepancies between the Tracker and the Portal for some reports.",
        "Corrected an issue that caused the assignment details in the Calendar to display incorrectly.",
      ],
    },
    {
      version: "0.8.19",
      date: "02-20-2026",
      changes: [
        "Updated the 3 first available reports in Financial Reports: Revenue Summary, Revenue by Client, and Revenue by Service Type.",
      ],
    },
    {
      version: "0.8.18",
      date: "02-18-2026",
      changes: [
        "Updated the Invoice Status to show if the invoice is Paid, Not Paid, or Overdue (Not Paid and more than 30 days past due date).",
        "Updated the Invoice Profile to show additional details on the side.",
      ],
    },
    {
      version: "0.8.17",
      date: "02-17-2026",
      changes: [
        "Added holidays to the Calendar and Interpretation Creation: Helps knowing if the assignment day is a holiday, to avoid scheduling conflicts.",
        "Holidays can be managed from Settings > Holidays: Create, Edit, and Delete.",
        "Added all known holidays in the U.S. up to 2035.",
      ],
    },
    {
      version: "0.8.16",
      date: "02-13-2026",
      changes: [
        "Added Financial Reports 3/7: Revenue Summary, Revenue by Client, and Revenue by Service Type.",
        "When Interpretation assignments are created, and for ease of access, an event will also be created on the Operations Outlook Calendar.",
      ],
    },
    {
      version: "0.8.15",
      date: "02-12-2026",
      changes: [
        "Added Density, Columns, and Export options to the Translations, Other Services lists.",
      ],
    },
    {
      version: "0.8.15",
      date: "02-11-2026",
      changes: [
        "Added Density, Columns, and Export options to the Interpretations list.",
        "Fixed an issue that caused manually set Account Charges to be overridden by the automatic calculation when editing an assignment.",
        "Fixed an issue that caused 2 Hours Minimum to be overridden by the automatic calculation when editing an assignment.",
        "Updated the whole Portal to be responsive and mobile friendly.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.14",
      date: "02-10-2026",
      changes: [
        "Updated the Calendar screen to be responsive and mobile friendly.",
        "Updated the AuditLog to be more efficient, save more space and show more relevant information.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.13",
      date: "02-09-2026",
      changes: [
        "Corrected an issue that caused new invoices to be created with the date of their services, instead of today's date.",
        "Corrected an issue that caused the wrong Project Number to be assigned to Interpretation Projects when Editing.",
        "Separated Total Linguist Pay from Total Cost in the Interpretation assignments.",
        "Added a new field 'Equipment Cost' to Interpretation assignments to account for equipment-related expenses.",
        "Added Unit Type, Unit Count and Unit Rate to Translation assignments and Other Services assignments.",
        "Units will be set from Settings > Unit Types.",
        "Units will allow better tracking of Translation assignments and Other Services assignments, as well as invoicing clients based on different unit types.",
      ],
    },
    {
      version: "0.8.12",
      date: "02-06-2026",
      changes: [
        "Completely removed the old IP link: 135.148.148.37. It now redirects to the new domain: admin.communicationessentialsllc.com.",
      ],
    },
    {
      version: "0.8.11",
      date: "02-04-2026",
      changes: [
        "Fixed multiple issues with the OPI logs and Invoices.",
        "Fixed an issue that caused OPI assignment creation to fail for clients with OPI Minimum & no calls during the month.",
        "Fixed an issue that caused the time in the Calendar Assignment Details to show in 24-hour format instead of 12-hour AM/PM format.",
      ],
    },
    {
      version: "0.8.10",
      date: "02-03-2026",
      changes: [
        "Fixed an issue that caused the Linguist Payments screen to account for deleted assignments.",
      ],
    },
    {
      version: "0.8.09",
      date: "01-30-2026",
      changes: [
        "Added HyperLink feature to the Communication Templates editor, Reminders, and Details overlays.",
        "Send Details for interpretations will always be visible now (Regardless of Assignment Status or Date).",
        "All assignments can be separately marked as paid, regardless of the client type. (Won't be invoiceable later).",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.08",
      date: "01-29-2026",
      changes: [
        "Invoices now can have a payment link, and will show a QR code as well as a clickable link in the PDF export.",
        "Invoices will now have the ability to split Payment Fees from the service.",
        "Reminders and Details will now have only the Linguist's name, and will automatically adjust to the assignment type.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.07",
      date: "01-28-2026",
      changes: [
        "Updated the UI: Filters will now be showing on the left, and the buttons have labels.",
        "Added 'Reminders' to the Interpretations list screen: Allowing users to get assignments reminders to send to Linguists.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.06",
      date: "01-27-2026",
      changes: [
        "Updated the Email Templates to 'Communication Templates' to have SMS templates included.",
        "Corrected an issue that caused Interpretation Projects to be created with the wrong Project Number.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.05",
      date: "01-26-2026",
      changes: [
        "Added the infrastructure of the Reports section.",
        "Added Amer's and the Operations Digital Cards.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.04",
      date: "01-23-2026",
      changes: [
        "Corrected an issue that caused Linguist Pay to be wrong when the assignment is Simultaneous.",
        "Updated the Linguists' Payments screen to show the remaining balance to be paid.",
        "Linguists' Payments now save which services have been paid for each linguist.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.03",
      date: "01-22-2026",
      changes: [
        "Added all the Expenses data from the Tracker, dating back to 2023.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.02",
      date: "01-21-2026",
      changes: [
        "Adjusted Linguists Rates to support decimal values.",
        "Corrected an issue that prevented zeroing the charges when the assignment is cancelled.",
        "Updated the invoices to set ServicesDate based on related services and adjusted missing ServicesDate to one month before the invoice date.",
        "Added the ability to access the Interpretation assignment profile from the Calendar.",
        "Added the 'Copy Tracker Row' button in the Profile of all Services, allowing users to quick paste the assignment/project details from Portal to Tracker.",
        "Added the ability to create Categories and SubCategories for expenses in the Settings.",
        "Added Expenses Management for Admins: An expense can be created, edited, and tracked.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.01",
      date: "01-16-2026",
      changes: [
        "Added a feature to refresh data of an assignment or profile when another user updates it.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.8.0",
      date: "01-15-2026",
      changes: [
        "Added a feature to lock assignments when being edited by another Member.",
        "Added a feature to allow seeing who is also viewing other assignments, to avoid conflicts.",
        "Added Member Profile Image.",
        "Corrected an issue that caused the Service Creation overlay to open with no dropdowns data.",
        "Added Password Strength calculation when changing or resetting the Password.",
        "Improved the responsiveness of the Overlays.",
        "Improved the User Interface across the whole Portal.",
        "Corrected an issue that caused assignments to not be saved when Account Charge was zeroed out.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.16.1",
      date: "01-13-2026",
      changes: [
        "Improved the History keeping feature of the whole Portal.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.15",
      date: "01-12-2026",
      changes: [
        "Added a check to the Project Number to prevent duplications.",
        "Added a filter to the Calendar, allowing to show any combination os service statuses.",
        "Added the option to Split the languages in the Invoice.",
        "Corrected an issue that caused service Duplication to fail.",
        "Improved the Automatic Calculation of charges in New/Edit Service screen.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.14",
      date: "01-09-2026",
      changes: [
        "Improved the Invoice address and export process.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.13",
      date: "01-08-2026",
      changes: [
        "Added the ability to export Invoices with or without OPI details.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.12",
      date: "01-07-2026",
      changes: [
        "Corrected an issue that caused incorrect Project Numbers to be generated when editing an Interpretation.",
        "Corrected an issue that caused duplicated assignments to have incorrect Project Numbers.",
        "Allowed the changing of the Client Name and Address directly from the Invoice edit screen.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.11",
      date: "01-06-2026",
      changes: [
        "Corrected an issue that caused Times to be displayed incorrectly due to Timezone changes.",
        "Invoices can be marked as Paid, either from the Invoice Profile or from the Invoices list (multi-select supported).",
        "Private Persons assignments can be marked as Paid.",
        "Corrected an issue that caused N/A linguists to be recreated when setting Linguist to N/A in Interpretation Creation/Edit.",
        "Added a way to see the number of tasks for the user from the side menu.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.10",
      date: "01-05-2026",
      changes: [
        "Invoices are fully functional: Create, View, Edit, Delete, and Export as PDF.",
        "Invoice services' mileage can be split from the service itself.",
        "Interpretation Creation: Added the ability to use Mileage Rates from the settings, depending on the year of the assignment.",
        "Interpretation Creation: Now handles correctly linguists with updated rates.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.09",
      date: "01-02-2026",
      changes: [
        "Invoices Feature: Added the ability to create, view, and manage invoices directly within CE Portal.",
        "Invoices can be exported as PDF for easy sharing and record-keeping.",
        "Invoice Details can be",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.08",
      date: "12-30-2025",
      changes: [
        "Implemented Email Templates: Created customizable email templates for various scenarios such as assignment creation, completion, and reminders. Users can now select and edit email templates to suit their communication needs.",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.07",
      date: "12-26-2025",
      changes: [
        "Corrected an issue that caused new Translations to have Bengali as source language.",
        "Improved the Filters of all DataGrids to prevent issues when filtering amounts.",
        "Linguists Payments Total Amount filter now accepts numeric comparisons (>, <, >=, <=).",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.06",
      date: "12-25-2025",
      changes: [
        "Updated the Linguists' Payments to have more details.",
        "Added Auto-Generated Tasks feature, which automatically creates tasks based on specific conditions related to services and invoicing.",
        "Added the ability to adjust Interpretation assignments to allow durations less than 1 hour (for scheduling or calling assignments).",
        "Bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.05",
      date: "12-24-2025",
      changes: [
        "Added a redirect to 404 page when a user without Admin role tries to access Admin-only routes.",
        "Added a 404 Not Found page to inform users when they try to access a non-existent page.",
        "Added color coding to various Service Columns for better visual distinction.",
      ],
    },
    {
      version: "0.7.04",
      date: "12-23-2025",
      changes: [
        "Added Calendar screen to view assignments in a calendar format.",
        "Added Members management screen, allowing Admins to manage CE Portal members.",
        "Updated the Settings screen: Members can now change their Name, and update their passwords.",
        "Added the 'Forgot Password' feature to the Login screen, allowing users to reset their password via email.",
        "Various bug fixes and performance improvements.",
      ],
    },
    {
      version: "0.7.03",
      date: "12-18-2025",
      changes: [
        "Corrected an issue that prevented Editing or Completing assignments from the Dashboards.",
        "Corrected an issue that prevented all the Clients from showing when adding a new Interpretation assignment.",
        "Updated the UI's font to use the latest branding guidelines.",
      ],
    },
    {
      version: "0.7.02",
      date: "12-17-2025",
      changes: [
        "Corrected an issue that caused a black screen when trying to edit a Requestor",
        "Added the Context Menu to all the DataGrids in all the Dashboards.",
        "Corrected an issue that prevented Two Hour Minimum assignments to be created correctly.",
      ],
    },
    {
      version: "0.7.01",
      date: "12-16-2025",
      changes: [
        "Added Tasks to each service profile (Interpretations, Translations, Other Services), allowing users to create, assign, and manage tasks related to each service directly from its profile.",
        "Improved the Date Filter component to have month/day/year selection for more precise filtering.",
        "Corrected an issue that prevented the Clients that don't have any requestors from being displayed correctly.",
      ],
    },
    {
      version: "0.7.00",
      date: "12-15-2025",
      changes: [
        "Updated the UI colors to match the latest branding guidelines.",
        "Completely updated the UI of the Portal to make it more user-friendly and visually appealing.",
      ],
    },
    {
      version: "0.6.94",
      date: "12-12-2025",
      changes: [
        "Corrected an issue that prevented the Project Number from being set up in new Translation and Other Services assignments.",
        "Updated the Logo in the Login screen, Sidebar, and favicon to the latest CE Logo.",
        "Added a confirmation dialog when duplicating assignments to prevent accidental duplications.",
        "Improved the Context Menus across all services to enhance usability and accessibility.",
        "Corrected an issue that caused Translation assignments to have a wrong Linguist.",
        "Corrected an issue that prevented new Translation and Other Services to be created.",
        "Corrected an issue that prevented the Tasks to be deleted.",
        "Improved the Tasks Filtering system",
        "Added all the Linguists columns from the Tracker to the Portal.",
        "Corrected an issue that prevented the Linguist's Mode to be saved.",
      ],
    },
    {
      version: "0.6.93",
      date: "12-11-2025",
      changes: [
        "Filters across all DataGrids are now saved automatically, and will persist until manually cleared.",
        "Added Tasks feature to CE Portal, allowing users to create, assign, and manage tasks within the portal.\nTasks can be associated with specific entities such as Clients, Linguists, or Services.\nUsers can set due dates, priorities, and statuses for each task.\nAdded notifications for task assignments and upcoming due dates.\nImproved overall UI/UX for better navigation and usability.",
        "Added a Context Menu to the Services DataGrids in CE Portal, accessible via right-clicking on a row.\nThe Context Menu provides quick access to common actions such as Edit, Add Task or Duplicate, and more to come, depending on the entity type.",
        "Added Version Check feature to CE Portal, which periodically checks for new versions of the application.\nWhen a new version is detected, a notification is displayed to the user with an option to reload the page and update to the latest version.",
      ],
    },
    {
      version: "0.6.92",
      date: "12-10-2025",
      changes: [
        "Corrected the collapsed sidebar tooltip display issue: Tooltip now shows correctly when the sidebar is collapsed.",
        "Corrected an issue that prevented opening specific service profiles or client/linguist profiles by id from the link.",
        "Improved the Interpretation creation/update:",
        "- The Enter button works correctly to submit the form.",
        "- Corrected an issue that caused empty fields to have 0.00 values.",
        "- Added a checkbox for 2 Hours Minimum next to the Rate field, as some assignments are billed with a 2-hour minimum regardless of the linguists minimum charge.",
        "Corrected an issue that caused the Client Interpretation Rates by language to only show 3 exceptions, instead of all the exceptions added for that client.",
        "Improved the Client Interpretation Rates creation UI: The Exceptions are now scrollable down.",
        "Added the ability to clear each filter on its own in all the DataGrids.",
        "The filter icon for each column now indicates when a filter is active.",
      ],
    },
    {
      version: "0.6.91",
      date: "12-09-2025",
      changes: [
        "Improved Dashboard to prevent a white screen issue on load.",
        "Added a confirmation dialog for logout to prevent accidental sign-outs.",
        "Fixed the tooltip display issue on the sidebar icons in collapsed mode.",
        "Corrected the search behavior in the Date Filters across the Portal.",
      ],
    },
    {
      version: "0.6.90",
      date: "12-05-2025",
      changes: [
        "Improved the filtering and sorting performance in all DataGrids in CE Portal.",
        "Added links to the Client and Linguist profiles from the Interpretation, Translation, and Other Services profiles.",
        "Imported all the Invoices from the Tracker, dating back to 2023.",
        "Created the Invoices screen to manage all invoices in CE Portal.",
        "Added the ability to add invoices from the Interpretation, Translation, and Other Services services.",
        "Added a link to the Invoice profile from the Interpretation, Translation, and Other Services profiles.",
      ],
    },
    {
      version: "0.6.8",
      date: "11-28-2025",
      changes: [
        "Added the new filtering system to all DataGrids in CE Portal, mimicking the same filtering system in Excel.",
      ],
    },
    {
      version: "0.6.7",
      date: "11-27-2025",
      changes: [
        "Updated CE Portal's data to have the latest assignments from the Tracker as of 11-27-2025.",
        "Imported the invoices from the Tracker, dating back to 2023.",
        "Created the Invoices screen to manage all invoices in CE Portal.",
        "Improved the filtering options in the Invoices screen, mimicking the same filtering system in Excel.",
        "Applied the new Excel-like filtering system to all services lists (Translations, Interpretations, Other Services).",
        "Applied the new Excel-like filtering system to the Clients and Linguists lists.",
      ],
    },
    {
      version: "0.6.6",
      date: "10-06-2025",
      changes: [
        "Added OPI Minimum Charge to Client Creation/Edit screen [Can only be set if OPI is checked under Services].",
        "Added: Interpretation Rates for Clients: We can create different rates by language for each client.",
        "Added: OPI Logs upload to the Interpretations: Upon uploading the CSV logs from IM, CE Portal will process it and will add all the entries to the database, as well as allow the user to download the XLSX final sheet, and the PDF detailed logs.",
      ],
    },
    {
      version: "0.6.5",
      date: "08-19-2025",
      changes: [
        "History is now fully shown on all the services.",
        "Added totals for the currently selected months in the Translations, Interpretations, and Other Services lists.",
      ],
    },
    {
      version: "0.6.4",
      date: "08-15-2025",
      changes: [
        "Added Client Rate Discount, which helps reduce the client's rate and do all the calculations, in case of a cancellation or if rate reduction is needed.",
        "Filtering by Year and Months will now be saved [In All Services], and the filter will persist until changed/removed.",
        "Added a Note field in the Complete Interpretation assignment screen.",
      ],
    },
    {
      version: "0.6.3",
      date: "08-14-2025",
      changes: [
        "Added the ability to make an Interpretation assignment recurring (Daily, Weekly, or Monthly).",
      ],
    },
    {
      version: "0.6.2",
      date: "08-13-2025",
      changes: [
        "Corrected an issue where the 'Open Drive Folder' opened only in 'Translations 2025', now it opens even old projects from 2023 & 2024.",
        "Added the ability to delete Translation, Interpretation and Other Services assignments [In case one was created by mistake].",
      ],
    },
    {
      version: "0.6.1",
      date: "05-16-2025",
      changes: [
        "Interpretation Completion: Now the Actual Start and Actual End will be automatically filled by the Start and End times.",
        "All Services: Sorting is now being saved, your sorts will persist until you remove them.",
        ".",
        ".",
      ],
    },
    {
      version: "0.6.0",
      date: "04-29-2025",
      changes: [
        "Add Interpretation: Corrected an issue that caused Linguist Pay to be less than 1 hour.",
        "Linguist Pay & Account Charge now correctly round up to the next 15 minutes segment.",
        "Corrected an issue that caused issues when sorting by Time Start/End in Interpretations List.",
        "In Interpretation Profile, the 'Check Mileage' button now doesn't show if it's a virtual assignment.",
        "In Interpretation Profile, the 'Call' button for the Contact Person only shows if a phone number has been added.",
        "All Services: Filtering by a column now is saved, and your filters will now persist until you remove them manually (like in the Tracker).",
      ],
    },
    {
      version: "0.5.9",
      date: "04-23-2025",
      changes: [
        "Updated the UI.",
        "Added the Help page, which contains videos showing how to navigate through CE Portal.",
        "Moved the Changelog to the Help page.",
      ],
    },
    {
      version: "0.5.8",
      date: "04-21-2025",
      changes: [
        'Corrected Total Duration "Add Interpretation Assignment": It will now count the biggest between Actual Duration and Duration.',
        'Added the option to edit the Total Duration manually in "Add Interpretation Assignment".',
        "Corrected an issue that prevented the correct saving of Assignment Checking Date (All Services).",
        "Added time to the Note's Creation Date.",
        "Corrected an issue that prevented Completing Other Service assignments.",
        "Corrected an issue that prevented Delivery Date to be set correctly.",
        "Corrected an issue that prevented the saving of the Delivery Type, and Payment Type when Completing an assignment.",
        'Corrected the "New Client" form.',
        "Corrected an issue that caused the Linguist's Mode to not be saved.",
        "Corrected an issue that caused the Bottom Message to always show in the day theme.",
      ],
    },
    {
      version: "0.5.7",
      date: "04-16-2025",
      changes: [
        "Updated the Month & Year Picker in the Dashboard allowing multi month selection in the ALL Dashboard.",
      ],
    },
    {
      version: "0.5.6",
      date: "04-14-2025",
      changes: ["Updated the Month & Year Picker in All Services lists."],
    },
    {
      version: "0.5.5",
      date: "04-10-2025",
      changes: [
        "Corrected some dashboard issues with charts not showing for future dates.",
        "Corrected a server OS difference issue that caused Clients Dashboard to show no data.",
      ],
    },
    {
      version: "0.5.4",
      date: "04-09-2025",
      changes: [
        "Corrected an issue that prevented pages from showing after refreshing.",
      ],
    },
    {
      version: "0.5.3",
      date: "04-08-2025",
      changes: [
        "Added 'Change Category' to Services, allowing changing a category of a service after it's created.",
      ],
    },
    {
      version: "0.5.2",
      date: "04-07-2025",
      changes: [
        "Applied server-side corrections.",
        "Added Contact Person (Name and Number) To Interpretations, with the Quick Action to call them from CE Portal directly.",
        "Admin: Corrected Dashboard > Clients > Type: Clicking on N/A now correctly filters the list of clients.",
        "Admin: In Dashboard > General: Instead of 'Last Month', the Card will now show the name of the last month ('Mar' if this month if 'Apr').",
        "Admin: In Dashboard > General: Added a Trend icon, and ensured its color and the percentage color represents the trend.",
        "Replaced the Years filter in Interpretations, now it is possible to filter by both Month and Year.",
        "Replaced the Years filter in Translations, now it is possible to filter by both Month and Year.",
        "Replaced the Years filter in Other Services, now it is possible to filter by both Month and Year.",
        "Added Login Logs: Which allows knowing who logged in, and when, and help protecting access and knowing when someone from outside of CE tries to login.",
      ],
    },
    {
      version: "0.5.1",
      date: "04-04-2025",
      changes: [
        "Added: Changelog which allows tracking what's added and what's changed in the portal.",
        "Added: Placeholder screen for Management pages.",
        "Added: Bruteforce protection, limiting login requests to 5 requests per minute.",
        "Corrected Interpretation Quick Actions to only show if the phone number, email, or address are available.",
        "Corrected Translation Quick Actions to only show if the email is available.",
        "Corrected Other Services Quick Actions to only show if the email is available.",
        "Corrected the Add new Note button possition (Was under the Assignment Actions).",
        "Imported the latest assignments from the Tracker: CE Portal is up to date to Tracker as of 04-04-25 at 01PM EST.",
      ],
    },

    {
      version: "0.5.0",
      date: "04-03-2025",
      changes: ["Initial release of MVP as shown to Amer."],
    },
  ];

  // Function to open video link in a new tab
  const handleVideoClick = (link) => {
    window.open(link, "_blank");
  };

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        //borderRadius="12px"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          mb: 1,
          mx: 1,
          mt: 2,
          //boxShadow: theme.shadows[3],
        }}
      >
        {/* Tutorial Videos Section */}
        <Box mb={4}>
          <Typography
            variant="h4"
            fontWeight="bold"
            color={theme.palette.primary.main}
            sx={{ mb: 3 }}
          >
            Here are some videos to show you how certain features of CE Portal
            work
          </Typography>

          {tutorialVideos.map((category, categoryIndex) => (
            <Box key={categoryIndex} mb={4} sx={{ px: 1 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={theme.palette.primary.main}
                sx={{ mb: 2, pl: 1 }}
              >
                {category.category}
              </Typography>

              <Grid container spacing={2}>
                {category.videos.map((video, videoIndex) => (
                  <Grid key={videoIndex} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card
                      sx={{
                        height: "100%",
                        backgroundColor: theme.palette.background.default,
                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.15)",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.25)",
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleVideoClick(video.link)}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            component="div"
                            fontWeight="bold"
                            color={theme.palette.primary.main}
                            gutterBottom
                          >
                            {video.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={theme.palette.primary.text2}
                          >
                            {video.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>

        {/* Changelog Section */}
        <Box mt={6}>
          <Typography
            variant="h4"
            fontWeight="bold"
            color={theme.palette.primary.main}
            sx={{ mb: 3 }}
          >
            Changelog
          </Typography>

          {versions.map((version, index) => (
            <Box
              key={"" + index}
              sx={{
                mb: 2,
                p: 1,
              }}
            >
              <CardDashboardItemVerticalMulti
                title={"Version: " + version.version}
                subTitle={"Released on: " + version.date}
                content={version.changes}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Help;
